/**
 * Gauge chart builder (ADR-0079 §4.1, §4.4).
 *
 * Single-value gauge. Reads the value from the FIRST row of `value.column`.
 * `encoding.max` is required (the gauge axis upper bound); `encoding.min`
 * defaults to 0. `options.thresholds` (sorted by `value`) drive the colored
 * arc bands via `axisLine.lineStyle.color` color stops.
 */

import type { GaugeSpec } from "../echartsSpec.js";
import {
  buildNoDataGraphic,
  resolveColumnIndex,
} from "./shared.js";
import { buildFormatter } from "../valueFormat.js";
import { valueFormatToLegacy } from "../atlasTooltip.js";

type EChartsOption = Record<string, unknown>;

export function buildGaugeOption(
  spec: GaugeSpec,
  rows: unknown[][],
  dimensions: string[],
): EChartsOption {
  const isEmpty = rows.length === 0;

  const valueIdx = resolveColumnIndex(spec.encoding.value.column, dimensions);
  const value =
    valueIdx === null || rows.length === 0
      ? null
      : coerce(rows[0][valueIdx]);

  const min = spec.encoding.min ?? 0;
  // Validator enforces max is present; default to 100 only if a malformed
  // spec sneaks past (renderer must never throw).
  const max = spec.encoding.max ?? 100;

  // Build axisLine color stops from thresholds (if any). Each threshold
  // contributes one color stop; the segments span [prev → t.value/max].
  const colorStops = buildColorStops(spec.options.thresholds, min, max);

  const detailFormatter = spec.encoding.value.format
    ? buildFormatter(valueFormatToLegacy(spec.encoding.value.format))
    : ((v: unknown) => String(v));

  return {
    ...(spec.options.title
      ? {
          title: {
            text: spec.options.title,
            ...(spec.options.subtitle ? { subtext: spec.options.subtitle } : {}),
          },
        }
      : {}),
    series: [
      {
        type: "gauge",
        min,
        max,
        ...(colorStops ? { axisLine: { lineStyle: { color: colorStops } } } : {}),
        detail: {
          formatter: (v: unknown) => detailFormatter(v),
        },
        data: value != null ? [{ value }] : [],
      },
    ],
    ...(isEmpty || value == null ? { graphic: buildNoDataGraphic() } : {}),
  };
}

function buildColorStops(
  thresholds: GaugeSpec["options"]["thresholds"],
  min: number,
  max: number,
): Array<[number, string]> | null {
  if (!thresholds || thresholds.length === 0) return null;
  const span = max - min;
  if (span <= 0) return null;
  const sorted = [...thresholds].sort((a, b) => a.value - b.value);
  const stops: Array<[number, string]> = [];
  for (const t of sorted) {
    const stop = Math.max(0, Math.min(1, (t.value - min) / span));
    stops.push([stop, t.color]);
  }
  // Ensure final stop is at 1 (use last threshold's color).
  if (stops[stops.length - 1][0] < 1) {
    stops.push([1, sorted[sorted.length - 1].color]);
  }
  return stops;
}

function coerce(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
